import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Table } from '@digdir/designsystemet-react';
import type { WorkEnvironmentDocumentation } from '../../../api/workEnvironmentTypes';
import {
  evaluationText,
  measureFigures,
  measureRows,
  methodFigures,
  partIntro,
  planIntro,
  residualRows,
  riskRows,
  stepRows,
} from '../inspectionReportView';
import { Figures, ReportSection, StatusTag } from './ReportSection';
import classes from '../InspectionReport.module.css';

const K = 'workEnvironmentInspection';

type Props = { report: WorkEnvironmentDocumentation };

/** Part 3: every step of the survey, with the day and the log entry */
export const StepsPart = ({ report }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <ReportSection id="report-steps" part={3} title={t(`${K}.parts.steps`)} intro={t(`${K}.parts.stepsIntro`)}>
      <ol className={classes.steps}>
        {stepRows(report, t, i18n.language).map((row) => (
          <li key={row.step} className={classes.step} data-state={row.state}>
            <span aria-hidden="true" className={classes.stepNumber}>
              {row.number}
            </span>
            <span className="ds-label" data-size="sm">
              {row.date}
            </span>
            <span className={classes.rowText}>
              <span className="ds-paragraph" data-size="sm">
                {t(`workEnvironmentProcess.steps.${row.step}.title`)}
              </span>
              <span className="ds-paragraph" data-size="sm">
                {row.text}
              </span>
              {row.log && (
                <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                  {t(`${K}.evidence.withLog`, { part: 8, ids: row.log })}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </ReportSection>
  );
};

/** Part 4: the calculated and the assessed level of every area, and the assessment */
export const RiskPart = ({ report }: Props): ReactElement => {
  const { t } = useTranslation();
  const rows = riskRows(report, t);
  const evaluated = report.evaluation !== null;
  return (
    <ReportSection
      id="report-risk"
      part={4}
      title={t(`${K}.parts.risk`)}
      intro={partIntro(report, 'riskIntro', t)}
      newPage
    >
      <Figures items={methodFigures(report, t)} />
      <div className={classes.tableWrap}>
        <Table data-size="sm" className={classes.table}>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell scope="col">{t(`${K}.risk.area`)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(`${K}.risk.calculated`)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(`${K}.risk.assessed`)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(`${K}.risk.decision`)}</Table.HeaderCell>
              {evaluated && <Table.HeaderCell scope="col">{t(`${K}.risk.after`)}</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {rows.map((row) => (
              <Table.Row key={row.key}>
                <Table.HeaderCell scope="row">
                  <span className={classes.cellStack}>
                    <span>{row.name}</span>
                    {row.sensitive && (
                      <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                        {t(`${K}.risk.sensitiveNote`)}
                      </span>
                    )}
                  </span>
                </Table.HeaderCell>
                <Table.Cell>
                  <StatusTag {...row.calculated} />
                </Table.Cell>
                <Table.Cell>
                  <span className={classes.cellStack}>
                    <StatusTag {...row.assessed} />
                    {row.adjustment && (
                      <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                        {row.adjustment}
                      </span>
                    )}
                  </span>
                </Table.Cell>
                <Table.Cell>{row.decision}</Table.Cell>
                {evaluated && <Table.Cell>{row.after && <StatusTag {...row.after} />}</Table.Cell>}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      {report.assessment && (
        <div className={classes.assessments}>
          <div className={classes.assessment}>
            <Heading level={3} data-size="2xs">
              {t(`${K}.risk.shortLongTerm`)}
            </Heading>
            <Paragraph data-size="sm">{report.assessment.shortLongTerm}</Paragraph>
          </div>
          {report.assessment.combined && (
            <div className={classes.assessment}>
              <Heading level={3} data-size="2xs">
                {t(`${K}.risk.combined`)}
              </Heading>
              <Paragraph data-size="sm">{report.assessment.combined}</Paragraph>
            </div>
          )}
        </div>
      )}
    </ReportSection>
  );
};

/** Part 5: the approved plans, every measure with owner and deadline, and the evaluation */
export const PlansPart = ({ report }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const rows = measureRows(report, t, i18n.language);
  const residual = residualRows(report, t, i18n.language);
  return (
    <ReportSection id="report-plans" part={5} title={t(`${K}.parts.plans`)} intro={planIntro(report, t)}>
      <Figures items={measureFigures(report, t)} />
      {rows.length === 0 ? (
        <Paragraph data-size="sm">{t(`${K}.measures.none`)}</Paragraph>
      ) : (
        <div className={classes.tableWrap}>
          <Table data-size="sm" className={classes.table}>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell scope="col">{t(`${K}.measures.measure`)}</Table.HeaderCell>
                <Table.HeaderCell scope="col">{t(`${K}.measures.area`)}</Table.HeaderCell>
                <Table.HeaderCell scope="col">{t(`${K}.measures.owner`)}</Table.HeaderCell>
                <Table.HeaderCell scope="col">{t(`${K}.measures.due`)}</Table.HeaderCell>
                <Table.HeaderCell scope="col">{t(`${K}.measures.status`)}</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {rows.map((row) => (
                <Table.Row key={row.key}>
                  <Table.Cell>
                    <span className={classes.cellStack}>
                      <span>{row.title}</span>
                      <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                        {row.source}
                      </span>
                    </span>
                  </Table.Cell>
                  <Table.Cell>{row.areas}</Table.Cell>
                  <Table.Cell>{row.owner}</Table.Cell>
                  <Table.Cell className={classes.nowrap}>{row.due}</Table.Cell>
                  <Table.Cell>
                    <StatusTag label={row.status} color={row.color} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
      <Paragraph data-size="sm" className={classes.callout}>
        {t(`${K}.evaluation.label`)}: {evaluationText(report, t, i18n.language)}
      </Paragraph>
      {residual.length > 0 && (
        <div className={classes.group}>
          <Heading level={3} data-size="xs">
            {t(`${K}.evaluation.residualTitle`)}
          </Heading>
          <ul className={classes.plainList}>
            {residual.map((area) => (
              <li key={area.name} className={classes.row}>
                <span className={classes.rowText}>
                  <span className="ds-label" data-size="sm">
                    {area.name}
                  </span>
                  <span className="ds-paragraph" data-size="sm">
                    {area.decision}
                  </span>
                </span>
                <StatusTag {...area.level} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </ReportSection>
  );
};
